import { type JSX, useCallback, useEffect, useState } from 'react';

import {
  ABSCHLIESSEND,
  FALLSTANDNAME,
  absagen as ladeAbsagen,
  alternativen as ladeAlternativen,
  kontaktAufgenommen,
  stornierenUndErstatten,
  umbuchen,
  type Absage,
  type Absagefall,
  type Alternative,
} from '../api/absagen';
import { ApiFehler } from '../api/fehler';
import { markenklasse } from '../marken';
import { Liste } from '../komponenten/Liste';
import { Meldung } from '../komponenten/Meldung';
import { Rueckfrage } from '../komponenten/Rueckfrage';
import { Segmente } from '../komponenten/Segmente';
import { useZaehler, zaehlwort } from '../komponenten/Seitenkontext';
import { alsEuro } from '../betrag';
import { Maskenverweis } from '../komponenten/Maskenverweis';

/**
 * Die Absageliste (BUC-129 bis BUC-132).
 *
 * ============================================================================
 * WARUM SIE EIN EIGENER MENÜPUNKT IST
 * ============================================================================
 *
 * BUC-129: Die betroffenen Buchungen „erscheinen in einer eigenen
 * Arbeitsliste". Wer eine Absage abarbeitet, telefoniert eine Liste ab — er
 * sucht nicht Buchung für Buchung über die Nummer.
 *
 * JE TERMIN EINE KARTE, JE BUCHUNG EINE ZEILE. Die Absage ist erst erledigt,
 * wenn alle Zeilen erledigt sind (BUC-132); die Karte sagt, wie viele noch
 * offen sind, und verschwindet aus der Ansicht „offen" erst dann.
 *
 * DREI HANDLUNGEN, KEINE MEHR. Kontakt vermerken, umbuchen, stornieren und
 * erstatten. Alles andere — Teilnehmer ändern, Rechnung ansehen — geschieht in
 * der Buchung, und die Zeile führt dorthin.
 *
 * ============================================================================
 * SEITENTYP LISTE (ÄA-41)
 * ============================================================================
 *
 * Alle Absagen stehen in EINER Tabellenkarte, je Termin ein Abschnitt mit
 * eigener Tabelle — die Zuordnung „je Termin" bleibt, die Kartenflut nicht.
 * Die drei Handlungen beginnen in der Zeile; was danach zu wählen ist
 * (Alternativtermin, Rückfrage vor dem Storno), steht in der Seitenleiste
 * neben der Tabelle. In der Zeile hätte die Liste der Alternativen die
 * Tabelle aufgerissen.
 */

function datum(wert: string): string {
  return new Date(wert).toLocaleString('de-DE', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Berlin',
  });
}

function standmarke(fall: Absagefall): JSX.Element {
  const fertig = ABSCHLIESSEND.includes(fall.stand);
  return (
    <>
      <span className={markenklasse(fertig ? 'abschluss' : 'laufend')}>
        {FALLSTANDNAME[fall.stand]}
      </span>
      {/* BUC-132: Der überfällige Fall ist eskaliert — Achtung, er ist zu tun. */}
      {!fertig && fall.eskaliertAm !== null && (
        <>
          {' '}
          <span className={markenklasse('aufmerksamkeit')}>eskaliert</span>
        </>
      )}
    </>
  );
}

type Bearbeitung = {
  readonly fall: Absagefall;
  readonly art: 'umbuchen' | 'stornieren';
  readonly termine?: readonly Alternative[];
};

/* CRM-103: die Rückfrage im Pop-up, bevor Umbuchung oder Storno geschieht. */
type Nachfrage =
  | { readonly art: 'umbuchen'; readonly fall: Absagefall; readonly termin: Alternative }
  | { readonly art: 'stornieren'; readonly fall: Absagefall };

export function Absagen({
  darfBearbeiten,
  beiBuchung,
  beiTerminakte,
}: {
  /** `termin:umbuchen` — der Support (AeA-04). Ohne das Recht: nur lesen. */
  readonly darfBearbeiten: boolean;
  readonly beiBuchung: (buchungsnummer: string) => void;
  /** BUC-131 (ÄW-DP-02): der Weg in die Akte des abgesagten Termins. */
  readonly beiTerminakte?: (termin: string) => void;
}): JSX.Element {
  const [liste, setzeListe] = useState<readonly Absage[] | null>(null);
  const [alle, setzeAlle] = useState(false);
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  const [meldung, setzeMeldung] = useState<string | null>(null);
  const [laeuft, setzeLaeuft] = useState(false);
  const [offen, setzeOffen] = useState<Bearbeitung | null>(null);
  const [nachfrage, setzeNachfrage] = useState<Nachfrage | null>(null);

  const holen = useCallback(async () => {
    try {
      setzeListe(await ladeAbsagen(alle));
      setzeFehler(null);
    } catch (f) {
      setzeListe([]);
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    }
  }, [alle]);

  useEffect(() => {
    void holen();
  }, [holen]);

  /* ÄA-41: der Zähler im Seitenkopf — erst, wenn geladen ist. */
  useZaehler(liste === null ? undefined : zaehlwort(liste.length, 'Absage', 'Absagen'));

  /**
   * Eine Handlung ausführen, die Meldung setzen, die Liste neu laden.
   *
   * Wirft beim Schreiben (CRM-103): Aus der Rückfrage heraus steht der Fehler
   * im Pop-up, und es bleibt offen. `tue` fängt ihn für die Knöpfe ohne
   * Rückfrage und schreibt ihn auf die Seite.
   */
  const schreibe = useCallback(
    async (was: () => Promise<string>) => {
      setzeLaeuft(true);
      setzeFehler(null);
      setzeMeldung(null);
      try {
        const text = await was();
        setzeNachfrage(null);
        setzeMeldung(text);
        setzeOffen(null);
        // Ein Fehler beim Nachladen landet über `holen` auf der Seite — die Handlung ist geschehen.
        await holen();
      } finally {
        setzeLaeuft(false);
      }
    },
    [holen],
  );

  const tue = useCallback(
    async (was: () => Promise<string>) => {
      try {
        await schreibe(was);
      } catch (f) {
        setzeFehler(f instanceof ApiFehler ? f : null);
        if (!(f instanceof ApiFehler)) throw f;
      }
    },
    [schreibe],
  );

  const umbuchenOeffnen = useCallback(async (fall: Absagefall) => {
    setzeFehler(null);
    try {
      setzeOffen({ fall, art: 'umbuchen', termine: await ladeAlternativen(fall.id) });
    } catch (f) {
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    }
  }, []);

  return (
    <>
      {fehler !== null && <Meldung fehler={fehler} />}

      <Liste
        vorschauBeschriftung="Umbuchen oder erstatten"
        filter={
          <Segmente
            beschriftung="Umfang"
            gewaehlt={alle ? 'alle' : 'offen'}
            beiWahl={(wert) => {
              setzeAlle(wert === 'alle');
            }}
            optionen={[
              { wert: 'offen', text: 'offen' },
              { wert: 'alle', text: 'alle' },
            ]}
          />
        }
        hinweise={
          <>
            <p className="hilfe">
              Die Buchungen abgesagter Termine — je Termin ein Abschnitt. Eine Absage ist erst
              erledigt, wenn jede Buchung umgebucht, erstattet oder storniert ist. Umbuchung und
              Storno kosten den Kunden nichts: Das Haus hat abgesagt.
            </p>
            {meldung !== null && (
              <p className="hinweiszeile" role="status">
                {meldung}
              </p>
            )}
          </>
        }
        vorschau={
          darfBearbeiten ? (
            <Bearbeitungsleiste
              offen={offen}
              laeuft={laeuft}
              beiAbbruch={() => {
                setzeOffen(null);
              }}
              frage={setzeNachfrage}
            />
          ) : undefined
        }
      >
        {liste === null && <p role="status">Wird geladen …</p>}
        {liste !== null && liste.length === 0 && (
          <p className="liste__leer">
            {alle ? 'Es wurde noch kein Termin abgesagt.' : 'Keine Absage wartet auf Bearbeitung.'}
          </p>
        )}

        {(liste ?? []).map((absage) => (
          <section key={absage.termin} aria-label={`Absage ${absage.produkt}`}>
            <div className="karte__titel">
              <h2>
                {beiTerminakte === undefined ? (
                  <>
                    {absage.produkt} · {datum(absage.beginn)}
                  </>
                ) : (
                  <Maskenverweis
                    maske={{ art: 'terminakte', id: absage.termin }}
                    title="Terminakte öffnen"
                    beiKlick={() => {
                      beiTerminakte(absage.termin);
                    }}
                  >
                    {absage.produkt} · {datum(absage.beginn)}
                  </Maskenverweis>
                )}
                {absage.ort === null ? '' : ` · ${absage.ort}`}
              </h2>
              {absage.abgeschlossen ? (
                <span className={markenklasse('abschluss')}>abgeschlossen</span>
              ) : (
                <span className={markenklasse('aufmerksamkeit')}>
                  {absage.offen} von {absage.faelle.length} offen
                </span>
              )}
            </div>
            {(absage.grund !== null || absage.abgesagtAm !== null) && (
              <p className="hinweiszeile">
                {absage.grund !== null && <>Grund: {absage.grund}</>}
                {absage.grund !== null && absage.abgesagtAm !== null && ' · '}
                {absage.abgesagtAm !== null && <>abgesagt am {datum(absage.abgesagtAm)}</>}
              </p>
            )}

            {/*
              RES-88: Die Einsätze sind mit der Absage storniert. Was davon eine
              Ausfallvergütung kostet, steht hier — die Grundlage, gegen die die
              Rechnung des Dienstleisters geprüft wird (RES-76).
            */}
            {(absage.einsaetze ?? []).length > 0 && (
              <p className="hilfe">
                Storniert: {(absage.einsaetze ?? []).map((e) => e.ressource).join(', ')}.{' '}
                {Number(absage.ausfallSumme) > 0 ? (
                  <strong>
                    Ausfallvergütung {alsEuro(absage.ausfallSumme)} (
                    {(absage.einsaetze ?? [])
                      .filter((e) => e.faellig)
                      .map((e) => `${e.ressource} ${alsEuro(e.betrag)}`)
                      .join(', ')}
                    )
                  </strong>
                ) : (
                  'Keine Ausfallvergütung fällig.'
                )}
              </p>
            )}

            <div className="tabellenhuelle tabellenhuelle--telefon">
              <table className="liste__tabelle" aria-label={`Buchungen zur Absage ${absage.produkt}`}>
                <thead>
                  <tr>
                    <th scope="col">Buchung</th>
                    <th scope="col">Kunde</th>
                    <th scope="col" className="zahl">
                      Plätze
                    </th>
                    <th scope="col" className="zahl">
                      Gezahlt
                    </th>
                    <th scope="col">Stand</th>
                    {darfBearbeiten && <th scope="col">Handlung</th>}
                  </tr>
                </thead>
                <tbody>
                  {absage.faelle.map((fall) => {
                    const fertig = ABSCHLIESSEND.includes(fall.stand);
                    const gewaehlt = offen?.fall.id === fall.id;
                    return (
                      <tr
                        key={fall.id}
                        className={
                          gewaehlt ? 'liste__zeile--gewaehlt' : fertig ? 'liste__zeile--blass' : undefined
                        }
                      >
                        <th scope="row">
                          <button
                            type="button"
                            className="liste__oeffnen liste__nummer"
                            onClick={() => {
                              beiBuchung(fall.buchungsnummer);
                            }}
                          >
                            {fall.buchungsnummer}
                          </button>
                        </th>
                        <td>
                          <span className="liste__name">{fall.kundenname}</span>
                          <br />
                          <span className="hilfe">
                            {[fall.telefon, fall.email].filter((w) => w !== null).join(' · ') ||
                              'kein Kontakt hinterlegt'}
                          </span>
                        </td>
                        <td className="zahl">{fall.plaetze}</td>
                        <td className="zahl">
                          {alsEuro(fall.gezahlt)}
                          {fall.rechnungen.length > 0 && (
                            <>
                              <br />
                              <span className="hilfe">Rechnung {fall.rechnungen.join(', ')}</span>
                            </>
                          )}
                        </td>
                        <td>
                          {standmarke(fall)}
                          {fall.zielTerminBeginn !== null && (
                            <>
                              <br />
                              <span className="hilfe liste__einzeilig">auf {datum(fall.zielTerminBeginn)}</span>
                            </>
                          )}
                          {fall.belegnummer !== null && (
                            <>
                              <br />
                              <span className="hilfe">Beleg {fall.belegnummer}</span>
                            </>
                          )}
                          {fall.notiz !== null && (
                            <>
                              <br />
                              <span className="hilfe">{fall.notiz}</span>
                            </>
                          )}
                        </td>
                        {darfBearbeiten && (
                          <td>
                            {/* BUC-130: kontaktieren, umbuchen, stornieren und erstatten. */}
                            {!fertig && !gewaehlt && (
                              <div className="knopfreihe knopfreihe--spalte">
                                <button
                                  type="button"
                                  className="knopf knopf--klein"
                                  disabled={laeuft}
                                  onClick={() =>
                                    void tue(async () => {
                                      await kontaktAufgenommen(fall.id, null);
                                      return `Kontakt zu ${fall.buchungsnummer} vermerkt.`;
                                    })
                                  }
                                >
                                  Kontakt aufgenommen
                                </button>
                                <button
                                  type="button"
                                  className="knopf knopf--klein"
                                  disabled={laeuft}
                                  onClick={() => void umbuchenOeffnen(fall)}
                                >
                                  Umbuchen …
                                </button>
                                <button
                                  type="button"
                                  className="knopf knopf--eingriff knopf--klein"
                                  disabled={laeuft}
                                  onClick={() => {
                                    setzeOffen({ fall, art: 'stornieren' });
                                  }}
                                >
                                  Stornieren und erstatten …
                                </button>
                              </div>
                            )}
                            {gewaehlt && <span className="hilfe">in Bearbeitung →</span>}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        ))}
      </Liste>

      {nachfrage !== null && (
        <Absagerueckfrage
          nachfrage={nachfrage}
          schreibe={schreibe}
          beiSchliessen={() => {
            setzeNachfrage(null);
          }}
        />
      )}
    </>
  );
}

/**
 * DIE RÜCKFRAGE VOR UMBUCHUNG UND STORNO (CRM-103, BUC-44, BUC-130).
 *
 * Sie nennt, was die Zeile weiß: Kunde, Plätze, Gezahltes, Rechnungen, den
 * Zieltermin. Beides kostet den Kunden nichts — das Haus hat abgesagt.
 */
function Absagerueckfrage({
  nachfrage,
  schreibe,
  beiSchliessen,
}: {
  readonly nachfrage: Nachfrage;
  readonly schreibe: (was: () => Promise<string>) => Promise<void>;
  readonly beiSchliessen: () => void;
}): JSX.Element {
  const { fall } = nachfrage;
  const unterzeile = `${fall.buchungsnummer} · ${fall.kundenname}`;
  const plaetze = `${String(fall.plaetze)} ${fall.plaetze === 1 ? 'Platz' : 'Plätze'}`;

  if (nachfrage.art === 'umbuchen') {
    const t = nachfrage.termin;
    return (
      <Rueckfrage
        frage="Buchung umbuchen?"
        unterzeile={unterzeile}
        tat="Umbuchen"
        beiBestaetigen={() =>
          schreibe(async () => {
            await umbuchen(fall.id, t.id, null);
            return `${fall.buchungsnummer} auf ${datum(t.beginn)} umgebucht — ohne Gebühr. Der Kunde bekommt eine Bestätigung.`;
          })
        }
        beiSchliessen={beiSchliessen}
      >
        <p>
          {plaetze} auf den Termin am {datum(t.beginn)}
          {t.ort === null ? '' : `, ${t.ort.bezeichnung}`} ({t.frei} frei) — ohne Gebühr. Der Kunde
          bekommt eine Bestätigung.
        </p>
      </Rueckfrage>
    );
  }

  return (
    <Rueckfrage
      frage="Buchung stornieren und erstatten?"
      unterzeile={unterzeile}
      tat="Stornieren und erstatten"
      beiBestaetigen={() =>
        schreibe(async () => {
          const e = await stornierenUndErstatten(fall.id, null);
          /* RNG-37 (AP-FA-05): ausgeführt oder noch zu überweisen — je Rückzahlung. */
          const geld = e.rueckzahlungen.map(
            (r) =>
              `${alsEuro(r.betrag)} ${r.status === 'bestaetigt' ? 'zurückgezahlt' : 'noch zurückzuzahlen'}`,
          );
          return [
            `${fall.buchungsnummer} storniert${
              e.belegnummer === null ? '' : `, Stornobeleg ${e.belegnummer}`
            }${geld.length > 0 ? `, ${geld.join(', ')}` : ''}.`,
            ...e.hinweise,
          ].join(' ');
        })
      }
      beiSchliessen={beiSchliessen}
    >
      <p>
        {plaetze} werden ohne Gebühr freigegeben. Gezahlt: {alsEuro(fall.gezahlt)}
        {fall.rechnungen.length > 0 ? `, Rechnung ${fall.rechnungen.join(', ')}` : ''}.
      </p>
      <p>
        Eine Rechnung allein zu dieser Buchung wird storniert, und der Kunde bekommt eine Bestätigung.
        Zahlungen über den Zahlungslink gehen sofort zurück; eine Überweisung zahlen Sie selbst
        zurück und bestätigen sie danach. Das lässt sich nicht zurücknehmen.
      </p>
    </Rueckfrage>
  );
}

/**
 * DIE SEITENLEISTE: der zweite Schritt einer Handlung aus der Zeile.
 *
 * Umbuchen zeigt die Alternativen (BUC-130), Stornieren fragt nach, bevor es
 * geschieht (BUC-44) — beides für GENAU den Fall, dessen Zeile markiert ist.
 */
function Bearbeitungsleiste({
  offen,
  laeuft,
  beiAbbruch,
  frage,
}: {
  readonly offen: Bearbeitung | null;
  readonly laeuft: boolean;
  readonly beiAbbruch: () => void;
  /** CRM-103: Umbuchen und Stornieren fragen im Pop-up nach. */
  readonly frage: (nachfrage: Nachfrage) => void;
}): JSX.Element {
  if (offen === null) {
    return (
      <p className="feld__hilfe">
        „Umbuchen …" und „Stornieren und erstatten …" in einer Zeile öffnen hier den nächsten
        Schritt: die freien Termine desselben Produkts oder die Rückfrage vor dem Storno.
      </p>
    );
  }

  const { fall } = offen;
  return (
    <>
      <div className="karte__titel">
        <h2>{offen.art === 'umbuchen' ? 'Umbuchen' : 'Stornieren und erstatten'}</h2>
        <small>{fall.buchungsnummer}</small>
      </div>
      <p className="hilfe">
        {fall.kundenname} · {fall.plaetze} {fall.plaetze === 1 ? 'Platz' : 'Plätze'} ·{' '}
        {alsEuro(fall.gezahlt)} gezahlt
      </p>

      {offen.art === 'umbuchen' &&
        ((offen.termine ?? []).length === 0 ? (
          <p className="hilfe">
            Kein Termin desselben Produkts hat {fall.plaetze} Plätze frei. Legen Sie einen an oder
            stornieren Sie.
          </p>
        ) : (
          <ul className="kanalliste" aria-label="Alternativtermine">
            {(offen.termine ?? []).map((t) => (
              <li key={t.id}>
                {datum(t.beginn)}
                {t.ort === null ? '' : `, ${t.ort.bezeichnung}`} · {t.frei} frei{' '}
                <button
                  type="button"
                  className="knopf knopf--klein"
                  disabled={laeuft}
                  onClick={() => {
                    frage({ art: 'umbuchen', fall, termin: t });
                  }}
                >
                  Hierhin umbuchen …
                </button>
              </li>
            ))}
          </ul>
        ))}

      {offen.art === 'stornieren' && (
        <p className="hilfe">
          Die Plätze werden freigegeben, eine Rechnung allein zu dieser Buchung wird storniert, und
          bezahlte Beträge werden zur Rückzahlung auf dem Zahlungsweg vermerkt. Das lässt sich
          nicht zurücknehmen.
        </p>
      )}

      <div className="knopfreihe">
        <button type="button" className="knopf knopf--klein" onClick={beiAbbruch}>
          Abbrechen
        </button>
        {offen.art === 'stornieren' && (
          <button
            type="button"
            className="knopf knopf--eingriff knopf--klein"
            disabled={laeuft}
            onClick={() => {
              frage({ art: 'stornieren', fall });
            }}
          >
            Stornieren und erstatten …
          </button>
        )}
      </div>
    </>
  );
}
